import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import {
  ArrowRight,
  Target,
  Users,
  Shield,
  Lightbulb,
  BookOpen,
  CheckCircle2,
} from "lucide-react";

const WORKPLACE_IMAGE = "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80";

const values = [
  {
    icon: Target,
    title: "Practical Focus",
    description: "We focus on what works in real work environments, not theoretical concepts.",
  },
  {
    icon: Users,
    title: "Accessible Learning",
    description: "Our training is designed for everyone, regardless of technical background.",
  },
  {
    icon: Shield,
    title: "Safety & Responsibility",
    description: "We emphasise safe, ethical, and responsible use of AI in all our workshops.",
  },
  {
    icon: Lightbulb,
    title: "Confidence Building",
    description: "We help learners build genuine confidence with AI tools they can use immediately.",
  },
];

const approach = [
  "Clear explanations without jargon",
  "Hands-on practice with real scenarios",
  "Relevant examples for your industry",
  "Focus on immediate practical application",
  "Safe space for questions and learning",
  "Support for learners at all levels",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="page-hero text-white py-16 md:py-20">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl">
              <h1 className="heading-display text-white mb-5 font-['Manrope']">
                About Be AI Ready
              </h1>
              <p className="text-lg md:text-xl text-[#94A3B8] leading-relaxed">
                Helping professionals and businesses become AI ready through practical, 
                clear, and confidence-building education.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white section-spacing" data-testid="mission-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <AnimatedSection>
              <h2 className="heading-section text-[#0A192F] mb-6 font-['Manrope']">
                Our Mission
              </h2>
              <p className="text-body-lg mb-5">
                Be AI Ready was created to help people and businesses understand and use AI 
                in a practical way. We believe that AI skills should be accessible to everyone, 
                not just technical specialists.
              </p>
              <p className="text-body mb-5">
                The focus is on building confidence, improving productivity, and helping 
                learners apply AI in real work without unnecessary complexity.
              </p>
              <p className="text-body">
                We're here to bridge the gap between AI technology and practical workplace 
                application, helping Australian professionals and businesses stay competitive 
                in a changing world.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <img
                src={WORKPLACE_IMAGE}
                alt="Modern professional workplace environment for AI training in Australia"
                className="w-full h-auto rounded-xl shadow-lg object-cover aspect-[4/3]"
                data-testid="about-image"
                loading="lazy"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F8FAFC] section-spacing" data-testid="values-section">
        <div className="container-main">
          <AnimatedSection>
            <h2 className="heading-section text-[#0A192F] mb-10 md:mb-12 font-['Manrope'] text-center">
              What We Stand For
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((value, index) => (
              <AnimatedSection key={index} delay={index * 0.08}>
                <div className="feature-card h-full text-center">
                  <div className="icon-wrapper mx-auto mb-4">
                    <value.icon size={22} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A192F] mb-2.5 font-['Manrope']">
                    {value.title}
                  </h3>
                  <p className="text-body-sm">{value.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="bg-white section-spacing" data-testid="approach-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope'] text-center">
                Our Teaching Approach
              </h2>
              <p className="text-body-lg text-center mb-10">
                We believe the best way to learn AI is through doing. Our workshops are 
                designed to give you practical skills you can use straight away.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 md:p-8 bg-[#F8FAFC] rounded-2xl border border-[#E2E8F0]">
                {approach.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#008A90] flex-shrink-0 mt-0.5" size={18} />
                    <p className="text-body">{item}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="page-hero text-white section-spacing" data-testid="who-we-help-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <BookOpen className="mx-auto mb-5 text-[#008A90]" size={40} />
              <h2 className="heading-section text-white mb-5 font-['Manrope']">
                Who We Help
              </h2>
              <p className="text-lg text-[#94A3B8] leading-relaxed mb-8">
                Our workshops are designed for Australian professionals, managers, business 
                owners, small businesses, and teams who want to learn how to use AI 
                effectively in their work. No technical background required.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {["Professionals", "Managers", "Business Owners", "Small Businesses", "Teams"].map((item) => (
                  <span
                    key={item}
                    className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium border border-white/10"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white section-spacing" data-testid="about-cta-section">
        <div className="container-main">
          <AnimatedSection>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="heading-section text-[#0A192F] mb-5 font-['Manrope']">
                Ready to learn with us?
              </h2>
              <p className="text-body-lg mb-8">
                Get in touch to discuss how we can help you or your team become AI ready.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/workshops">
                  <Button 
                    className="bg-[#0A192F] hover:bg-[#112240] text-white font-semibold px-7 py-6 transition-all hover:-translate-y-0.5"
                    data-testid="about-view-workshops-btn"
                  >
                    View Our Workshops
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    variant="outline"
                    className="border-2 border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white font-semibold px-7 py-6 transition-all hover:-translate-y-0.5"
                    data-testid="about-contact-btn"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
